/* ============================================================
   LÜFT — INTERACTIONS
   Lightweight ES module. No frameworks.
   ============================================================ */

(() => {
  'use strict';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isTouch = window.matchMedia('(pointer: coarse)').matches;

  /* === 1. REVEAL ON SCROLL === */
  function initReveal() {
    if (reduced) {
      document.querySelectorAll('.luft-reveal,.luft-image-reveal').forEach(el => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    document.querySelectorAll('.luft-reveal,.luft-image-reveal').forEach(el => io.observe(el));
  }

  /* === 2. SPLIT LETTER ANIMATIONS === */
  function initSplit() {
    if (reduced) return;
    document.querySelectorAll('[data-luft-split]').forEach(el => {
      if (el.dataset.luftSplitDone) return;
      const text = el.textContent;
      el.textContent = '';
      [...text].forEach((char, i) => {
        const span = document.createElement('span');
        span.className = 'luft-split-letter';
        span.style.transitionDelay = `${i * 25}ms`;
        span.textContent = char === ' ' ? ' ' : char;
        el.appendChild(span);
      });
      el.dataset.luftSplitDone = '1';
    });
  }

  /* === 3. CUSTOM CURSOR === */
  function initCursor() {
    if (isTouch || reduced) return;
    const cursor = document.createElement('div');
    cursor.className = 'luft-cursor';
    cursor.setAttribute('aria-hidden', 'true');
    document.body.appendChild(cursor);
    document.body.classList.add('has-custom-cursor');

    let mx = 0, my = 0, cx = 0, cy = 0;
    window.addEventListener('mousemove', (e) => { mx = e.clientX; my = e.clientY; }, { passive: true });

    function loop() {
      cx += (mx - cx) * 0.22;
      cy += (my - cy) * 0.22;
      cursor.style.transform = `translate(${cx}px, ${cy}px) translate(-50%, -50%)`;
      requestAnimationFrame(loop);
    }
    loop();

    // Hover state on interactive elements
    document.addEventListener('mouseover', (e) => {
      const t = e.target.closest('a, button, .luft-magnetic, [data-luft-hover]');
      if (t) cursor.classList.add('is-hover');
    });
    document.addEventListener('mouseout', (e) => {
      const t = e.target.closest('a, button, .luft-magnetic, [data-luft-hover]');
      if (t) cursor.classList.remove('is-hover');
    });
  }

  /* === 4. MAGNETIC HOVER === */
  function initMagnetic() {
    if (isTouch || reduced) return;
    document.querySelectorAll('.luft-magnetic').forEach(el => {
      const strength = parseFloat(el.dataset.magnetic || 0.25);
      el.addEventListener('mousemove', (e) => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - r.left - r.width / 2) * strength;
        const y = (e.clientY - r.top - r.height / 2) * strength;
        el.style.transform = `translate(${x}px, ${y}px)`;
      });
      el.addEventListener('mouseleave', () => { el.style.transform = ''; });
    });
  }

  /* === 5. PARALLAX === */
  function initParallax() {
    if (reduced) return;
    const items = document.querySelectorAll('.luft-parallax');
    if (!items.length) return;
    function tick() {
      items.forEach(el => {
        const r = el.getBoundingClientRect();
        const speed = parseFloat(el.dataset.parallax || 0.15);
        const center = r.top + r.height / 2;
        const offset = (window.innerHeight / 2 - center) * speed;
        el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
      });
      requestAnimationFrame(tick);
    }
    tick();
  }

  /* === 6. TILT 3D === */
  function initTilt() {
    if (isTouch || reduced) return;
    document.querySelectorAll('.luft-tilt').forEach(el => {
      const max = 5;
      el.addEventListener('mousemove', (e) => {
        const r = el.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width;
        const py = (e.clientY - r.top) / r.height;
        const rx = (py - 0.5) * -max * 2;
        const ry = (px - 0.5) *  max * 2;
        el.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg)`;
      });
      el.addEventListener('mouseleave', () => { el.style.transform = ''; });
    });
  }

  /* === 7. HEADER HIDE ON SCROLL DOWN === */
  function initHeaderHide() {
    const header = document.querySelector('.luft-header');
    if (!header) return;
    let lastY = window.scrollY;
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const y = window.scrollY;
          if (y > 200 && y > lastY) header.classList.add('is-hidden');
          else header.classList.remove('is-hidden');
          lastY = y;
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* === 8. STICKY MICRO-CTA === */
  function initStickyCta() {
    const cta = document.querySelector('.luft-sticky-cta');
    if (!cta) return;
    const offerSection = document.querySelector('#luft-offer, .luft-offer-section');
    let visible = false;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      const should = y > 600 && (!offerSection || offerSection.getBoundingClientRect().top > 200);
      if (should !== visible) {
        cta.classList.toggle('is-visible', should);
        visible = should;
      }
    }, { passive: true });
  }

  /* === 9. EXIT INTENT === */
  function initExitIntent() {
    const exit = document.querySelector('.luft-exit');
    if (!exit) return;
    if (sessionStorage.getItem('luft-exit-shown')) return;
    if (isTouch) return;
    document.addEventListener('mouseout', (e) => {
      if (e.clientY <= 0 && !sessionStorage.getItem('luft-exit-shown')) {
        exit.classList.add('is-open');
        sessionStorage.setItem('luft-exit-shown', '1');
      }
    });
    exit.querySelectorAll('[data-luft-exit-close]').forEach(b => {
      b.addEventListener('click', () => exit.classList.remove('is-open'));
    });
  }

  /* === 10. COOKIE BANNER === */
  function initCookie() {
    const banner = document.querySelector('.luft-cookie');
    if (!banner) return;
    if (localStorage.getItem('luft-cookies')) return;
    setTimeout(() => banner.classList.add('is-open'), 1200);
    banner.querySelectorAll('[data-luft-cookie]').forEach(b => {
      b.addEventListener('click', () => {
        localStorage.setItem('luft-cookies', b.dataset.luftCookie);
        banner.classList.remove('is-open');
      });
    });
  }

  /* === 11. DRAWERS (cart + mobile menu) === */
  function initDrawers() {
    document.querySelectorAll('[data-luft-drawer-open]').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.luftDrawerOpen;
        const drawer = document.getElementById(id);
        const back = document.querySelector(`[data-luft-drawer-backdrop="${id}"]`);
        if (drawer) drawer.classList.add('is-open');
        if (back) back.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      });
    });
    document.querySelectorAll('[data-luft-drawer-close], .luft-drawer__backdrop').forEach(el => {
      el.addEventListener('click', () => {
        document.querySelectorAll('.luft-drawer.is-open, .luft-drawer__backdrop.is-open').forEach(d => d.classList.remove('is-open'));
        document.body.style.overflow = '';
      });
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        document.querySelectorAll('.luft-drawer.is-open, .luft-drawer__backdrop.is-open, .luft-exit.is-open').forEach(d => d.classList.remove('is-open'));
        document.body.style.overflow = '';
      }
    });
  }

  /* === 12. PRELOADER === */
  function initPreloader() {
    const pre = document.querySelector('.luft-preloader');
    if (!pre) return;
    function hide() { pre.classList.add('is-out'); setTimeout(() => pre.remove(), 800); }
    if (document.readyState === 'complete') setTimeout(hide, 1600);
    else window.addEventListener('load', () => setTimeout(hide, 1600));
    pre.addEventListener('click', hide);
  }

  /* === 13. BEFORE/AFTER SLIDER === */
  function initBeforeAfter() {
    document.querySelectorAll('.luft-ba').forEach(slider => {
      const handle = slider.querySelector('.luft-ba__handle');
      const after = slider.querySelector('.luft-ba__after');
      if (!handle || !after) return;
      let dragging = false;
      const move = (clientX) => {
        const r = slider.getBoundingClientRect();
        let pct = ((clientX - r.left) / r.width) * 100;
        pct = Math.max(0, Math.min(100, pct));
        handle.style.left = pct + '%';
        after.style.clipPath = `inset(0 ${100 - pct}% 0 0)`;
      };
      const start = (e) => { dragging = true; e.preventDefault(); };
      const drag = (e) => { if (!dragging) return; move((e.touches ? e.touches[0].clientX : e.clientX)); };
      const stop = () => { dragging = false; };
      handle.addEventListener('mousedown', start);
      handle.addEventListener('touchstart', start, { passive: false });
      window.addEventListener('mousemove', drag);
      window.addEventListener('touchmove', drag);
      window.addEventListener('mouseup', stop);
      window.addEventListener('touchend', stop);
    });
  }

  /* === 14. COUNTDOWN AUTHENTIC === */
  function initCountdowns() {
    document.querySelectorAll('[data-luft-countdown]').forEach(el => {
      const target = new Date(el.dataset.luftCountdown).getTime();
      if (isNaN(target)) return;
      function update() {
        const diff = target - Date.now();
        if (diff <= 0) { el.textContent = '00:00:00:00'; return; }
        const d = Math.floor(diff / 86400000);
        const h = Math.floor((diff % 86400000) / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        const s = Math.floor((diff % 60000) / 1000);
        const pad = (n) => String(n).padStart(2, '0');
        el.textContent = `${pad(d)} : ${pad(h)} : ${pad(m)} : ${pad(s)}`;
      }
      update();
      setInterval(update, 1000);
    });
  }

  /* === 15. LIVE PURCHASE FEED ROTATOR === */
  function initLiveFeed() {
    const feed = document.querySelector('.luft-live-feed');
    if (!feed) return;
    const items = Array.from(feed.querySelectorAll('.luft-live-feed__item'));
    if (items.length < 2) return;
    let i = 0;
    items.forEach((it, k) => { it.style.display = k === 0 ? '' : 'none'; });
    setInterval(() => {
      items[i].style.display = 'none';
      i = (i + 1) % items.length;
      items[i].style.display = '';
    }, 9000);
  }

  /* === INIT === */
  function init() {
    initReveal();
    initSplit();
    // initCursor();  // disabled — too distracting
    initMagnetic();
    initParallax();
    initTilt();
    initHeaderHide();
    initStickyCta();
    initExitIntent();
    initCookie();
    initDrawers();
    initPreloader();
    initBeforeAfter();
    initCountdowns();
    initLiveFeed();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
